import React from "react";
import { HC, tone, label } from "./theme";

export const Card = ({ children, className = "", pad = true, testid, ...rest }) => (
  <div data-testid={testid} className={`rounded-2xl bg-white border ${pad ? "p-5" : ""} ${className}`}
    style={{ borderColor: HC.border }} {...rest}>{children}</div>
);

export const SectionHead = ({ title, sub, right }) => (
  <div className="flex items-start justify-between gap-3 mb-4">
    <div className="min-w-0">
      <h2 className="font-brand font-bold text-base md:text-lg leading-tight" style={{ color: HC.text }}>{title}</h2>
      {sub && <div className="text-xs mt-0.5" style={{ color: HC.soft }}>{sub}</div>}
    </div>
    {right}
  </div>
);

export const Pill = ({ status, text, testid }) => {
  const [c, bg] = tone(status);
  return (
    <span data-testid={testid} className="inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ background: bg, color: c }}>{text || label(status)}</span>
  );
};

export const Btn = ({ variant = "primary", children, className = "", ...rest }) => {
  const styles = {
    primary: { background: HC.burgundy, color: "#fff", border: `1px solid ${HC.burgundy}` },
    outline: { background: "#fff", color: HC.text, border: `1px solid ${HC.border}` },
    gold: { background: HC.gold, color: "#3B0D0D", border: `1px solid ${HC.gold}` },
    ghost: { background: "transparent", color: HC.burgundy, border: "1px solid transparent" },
    danger: { background: "#fff", color: HC.red, border: `1px solid ${HC.redTint}` },
  }[variant];
  return (
    <button {...rest} style={{ ...styles, ...(rest.style || {}) }}
      className={`inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>
      {children}
    </button>
  );
};

export const Field = ({ label: lbl, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] font-bold tracking-[0.14em] mb-1.5" style={{ color: HC.soft }}>{(lbl || "").toUpperCase()}</span>
    {children}
  </label>
);

export const Input = (props) => (
  <input {...props} className={`w-full h-10 px-3 rounded-xl text-sm bg-white outline-none focus:ring-2 ${props.className || ""}`}
    style={{ border: `1px solid ${HC.border}`, color: HC.text, ...(props.style || {}) }} />
);

export const Select = (props) => (
  <select {...props} className={`w-full h-10 px-3 rounded-xl text-sm bg-white outline-none ${props.className || ""}`}
    style={{ border: `1px solid ${HC.border}`, color: HC.text, ...(props.style || {}) }} />
);

export const Empty = ({ text = "No records yet" }) => (
  <div className="py-10 text-center text-sm" style={{ color: HC.soft }}>{text}</div>
);

export const Table = ({ columns, rows, rowKey = (r, i) => r.id || i, onRowClick, testid, empty }) => (
  <div className="overflow-x-auto -mx-5 px-5" data-testid={testid}>
    {rows?.length ? (
      <table className="w-full text-sm">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="text-left text-[10px] font-bold tracking-[0.14em] pb-3 pr-4 whitespace-nowrap"
                style={{ color: HC.soft, textAlign: c.align || "left" }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)} onClick={onRowClick ? () => onRowClick(r) : undefined}
              data-testid={`row-${rowKey(r, i)}`}
              className={`border-t ${onRowClick ? "cursor-pointer hover:bg-[#FBF8F2]" : ""}`} style={{ borderColor: HC.borderSoft }}>
              {columns.map((c) => (
                <td key={c.key} className="py-3 pr-4 align-middle" style={{ color: HC.text, textAlign: c.align || "left" }}>
                  {c.render ? c.render(r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    ) : <Empty text={empty} />}
  </div>
);

export const Avatar = ({ name, size = 28, url }) => {
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return url ? (
    <img src={url} alt={name} className="rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <span className="inline-flex items-center justify-center rounded-full font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36, background: HC.goldSoft, color: HC.burgundy }}>{initials}</span>
  );
};
